import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ScrollText, BadgeCheck, HandCoins, ArrowRight } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StarField from "../components/StarField";
import { useSEO } from "../hooks/useSEO";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "No fear-based remedies",
    text: "We do not offer fear or anxiety-based remedies. No doomsday doshas, no pressure to buy gemstones or rituals. Your chart is a map, not a sentence.",
    span: "md:col-span-2",
  },
  {
    icon: ScrollText,
    title: "Radical transparency",
    text: "Every prediction shows its methodology — the chart data, the dasha periods, the reasoning.",
    span: "",
  },
  {
    icon: BadgeCheck,
    title: "AI, verified by humans",
    text: "Each piece of advice generated by our proprietary AI is verified by a qualified Vedic astrologer before it reaches you.",
    span: "",
  },
  {
    icon: HandCoins,
    title: "Money-back guarantee",
    text: "We offer you money-back guarantee if you are not satisfied. No questions, no forms, no friction.",
    span: "md:col-span-2",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Landing() {
  const [services, setServices] = useState([]);
  useSEO(
    "NAKSRA | Vedic Astrology Reports Rooted in the Nakshatras",
    "Plan career, money, relationship and relocation decisions with NAKSRA — Vedic astrology (Jyotish) reports with radical transparency, astrologer-verified AI, and a money-back guarantee."
  );

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1516571748831-5d81767b788d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxjb25zdGVsbGF0aW9uJTIwc3RhcnJ5JTIwbmlnaHQlMjBza3l8ZW58MHx8fHwxNzg3NTA1OTQ5fDA&ixlib=rb-4.1.0&q=85"
          alt="Star-filled night sky with constellations"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="grain absolute inset-0" />
        <StarField />
        <div className="relative mx-auto max-w-5xl px-6 py-32 text-center sm:py-40">
          <motion.p initial="hidden" animate="show" variants={fadeUp} className="font-heading text-xs font-medium uppercase tracking-[0.25em] text-[#D4AF37] sm:text-sm">
            Rooted in the 5,000-year-old science of the Nakshatras of Jyotish (Vedic astrology) aided by Sangha (the NAKSRA Community of world-wide NAKSRA members)
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ delay: 0.15 }}
            className="mt-6 font-heading text-[29px] font-medium leading-tight tracking-tight sm:text-[38px] lg:text-[48px]"
            data-testid="hero-banner"
          >
            Whether you want a one-off reading or wish to retain a Jyotish guru (human guru, not AI guru) to get periodic guidance on your key moves, Naksra is the place. Consider it your second home.
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ delay: 0.22 }}
            className="mx-auto mt-8 max-w-4xl font-heading text-xl font-bold uppercase tracking-[0.15em] text-[#D4AF37] sm:text-2xl"
            data-testid="hero-aspects-line"
          >
            Love, relationships, family, children, health, career, money, relocation or any other aspect, Naksra has your back
          </motion.p>
          <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/services"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98]"
            >
              One-off Reading
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/retain-a-guru"
              data-testid="hero-guru-cta"
              className="rounded-full border border-[#D4AF37]/60 px-8 py-3 text-sm font-semibold text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37]/10 active:scale-[0.98]"
            >
              Retain a Guru
            </Link>
            <Link
              to="/blog"
              data-testid="hero-journal-cta"
              className="rounded-full border border-white/20 px-8 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5 active:scale-[0.98]"
            >
              Visit the Sangha
            </Link>
          </motion.div>
          <motion.p initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.45 }} className="mt-8 text-sm text-gray-300" data-testid="hero-guarantee-note">
            All our reports and services come with a money-back guarantee so you can have peace of mind
          </motion.p>
        </div>
      </section>

      {/* Trust pillars — bento */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <p className="text-xs font-medium uppercase tracking-[0.35em] text-[#D4AF37]">Our Promise</p>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-medium tracking-tight sm:text-4xl">
            Astrology without the anxiety industry
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: i * 0.1 }}
              className={`rounded-lg border border-white/10 bg-[#0C0C10] p-8 transition-colors duration-300 hover:border-[#D4AF37]/40 ${p.span}`}
              data-testid={`pillar-${i}`}
            >
              <p.icon className="h-7 w-7 text-[#D4AF37]" />
              <h3 className="mt-5 font-heading text-2xl font-medium">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Methodology section */}
      <section className="border-y border-white/10 bg-[#0C0C10]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-24 md:grid-cols-2">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-xs font-medium uppercase tracking-[0.35em] text-[#D4AF37]">Methodology, Shown</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight sm:text-4xl">
              Ancient computation, modern rigor
            </h2>
            <p className="mt-6 leading-relaxed text-[#A1A1AA]">
              Every NAKSRA report is computed from your exact birth time and place using the Lahiri ayanamsa, Vimshottari dasha and classical Jyotish interpretive frameworks. Our proprietary AI drafts the analysis; a qualified Vedic astrologer verifies every conclusion before delivery.
            </p>
            <p className="mt-4 leading-relaxed text-[#A1A1AA]">
              Each report ends with a methodology appendix: the exact chart data, planetary positions and period calculations behind every statement — so you never have to take anything on faith.
            </p>
            <Link to="/services" data-testid="methodology-cta" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] transition-colors duration-300 hover:text-[#FBE18D]">
              See the reports <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.15 }} className="relative overflow-hidden rounded-lg border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1547931587-2841fef9d915?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxhbmNpZW50JTIwYXN0cm9sYWJlJTIwYnJhc3N8ZW58MHx8fHwxNzg3NTA1OTQ5fDA&ixlib=rb-4.1.0&q=85"
              alt="Ancient brass astrolabe instrument"
              className="h-80 w-full object-cover md:h-full"
            />
            <div className="absolute inset-0 bg-black/40" />
          </motion.div>
        </div>
      </section>

      {/* Services preview */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="flex items-end justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.35em] text-[#D4AF37]">Reports & Services</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight sm:text-4xl">Choose your reading</h2>
          </div>
          <Link to="/services" data-testid="services-view-all" className="hidden items-center gap-2 text-sm font-semibold text-[#D4AF37] transition-colors duration-300 hover:text-[#FBE18D] sm:inline-flex">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <motion.div key={s.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: i * 0.08 }}>
              <Link
                to={`/services/${s.id}`}
                data-testid={`service-card-${s.id}`}
                className="flex h-full flex-col rounded-lg border border-white/10 bg-[#0C0C10] p-6 transition-colors duration-300 hover:border-[#D4AF37]/40"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">{s.sanskrit}</p>
                <h3 className="mt-3 font-heading text-2xl font-medium">{s.name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#A1A1AA]">{s.tagline}</p>
                <p className="mt-6 font-heading text-3xl text-[#D4AF37]">${s.price}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Calm / no-fear section */}
      <section className="relative overflow-hidden border-t border-white/10">
        <img
          src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbWVkaXRhdGlvbiUyMHNpbGhvdWV0dGUlMjBzdW5yaXNlfGVufDB8fHx8MTc4NzUwNTk0OXww&ixlib=rb-4.1.0&q=85"
          alt="Calm meditation silhouette at sunrise"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative mx-auto max-w-4xl px-6 py-28">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-xs font-medium uppercase tracking-[0.35em] text-[#D4AF37]">Clarity, Not Fear</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight sm:text-4xl">
              A chart should calm you, not scare you
            </h2>
            <p className="mt-6 max-w-2xl leading-relaxed text-gray-200">
              Too much of modern astrology runs on anxiety — frightening predictions followed by expensive remedies. NAKSRA was built as the opposite: precise timing, honest reasoning, and advice you can act on with a clear head. And if a report doesn't meet that bar, your money comes back.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
